/*
 * Gift note line-item router (fulfillment redesign, 2026-08-26).
 *
 * The gift message no longer prints on the packing slip. Instead, when the buyer types a message on the
 * gifting product template and adds the box to their cart, this script:
 *   1. Intercepts that specific add-to-cart request (matched by the same properties[_gift]=true marker the
 *      template already submits, plus a non-empty properties[Gift message]).
 *   2. Resolves a dedicated $0 "Gift note" product at runtime via GET /products/gift-note.js. Never
 *      hardcodes a variant id, since the product does not exist in Admin yet at the time this ships.
 *   3. If that product does not exist (404, or any lookup failure), the original request is sent through
 *      completely untouched: the message stays on the box line exactly like before, no errors, nothing
 *      user-visible. The feature self-activates the moment the product is created.
 *   4. If it does exist, the message is moved off the box line (properties[_gift] stays on the box line as
 *      a marker; properties[Gift message] is stripped before the request is sent) and added as its own
 *      "Gift note" line, qty 1, carrying properties[Gift message] and properties[_gift]=true, so the
 *      warehouse pick list shows "Gift note x1" and a team member handwrites the card.
 *
 * De-dup: this only ever runs as a direct consequence of a matching add-to-cart submission. It never fires
 * on unrelated cart calls (quantity edits, cart page loads, checkout), so removing the note line from the
 * cart is never resurrected on unrelated activity. If a gift-note line already exists in the cart (e.g. a
 * second gifting box is added in the same session), its properties are updated in place via /cart/change.js
 * instead of adding a second line, so there is never more than one gift-note line per cart.
 */
(function () {
  if (window.__atomeGiftNoteHookInstalled) return;
  window.__atomeGiftNoteHookInstalled = true;

  if (typeof window.fetch !== 'function') return;

  var GIFT_NOTE_HANDLE = 'gift-note';
  var MSG_KEY = 'properties[Gift message]';
  var GIFT_KEY = 'properties[_gift]';
  var CART_ADD_RE = /\/cart\/add(?:\.js)?(?:\?|$)/;

  var origFetch = window.fetch.bind(window);
  var giftNoteLookup = null;

  // Resolves once per page load and is cached (including a failed/absent lookup), so repeated add-to-cart
  // clicks don't re-probe /products/gift-note.js every time.
  function resolveGiftNote() {
    if (!giftNoteLookup) {
      giftNoteLookup = origFetch('/products/' + GIFT_NOTE_HANDLE + '.js', {
        headers: { Accept: 'application/json' }
      })
        .then(function (res) {
          return res.ok ? res.json() : null;
        })
        .then(function (product) {
          if (!product || !product.variants || !product.variants.length) return null;
          return { productId: product.id, variantId: product.variants[0].id };
        })
        .catch(function () {
          return null;
        });
    }
    return giftNoteLookup;
  }

  // Best-effort cart-drawer refresh: reuses the same "sections" ids the theme's own add-to-cart request
  // already asked for (captured off the intercepted FormData), and swaps in the freshly rendered HTML the
  // same way Shopify's section-rendering API is designed to be consumed. Never throws; if no matching
  // #shopify-section-<id> node exists, it's a no-op and the buyer still sees the box added normally.
  function applySections(sections) {
    if (!sections) return;
    try {
      Object.keys(sections).forEach(function (id) {
        var el = document.getElementById('shopify-section-' + id);
        if (el) el.innerHTML = sections[id];
      });
    } catch (e) {
      // Cosmetic only: worst case the note line doesn't show in the drawer until it's reopened.
    }
  }

  function upsertGiftNoteLine(message, giftNote, sectionsParam) {
    return origFetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var existingLine = null;
        var items = (cart && cart.items) || [];

        for (var i = 0; i < items.length; i++) {
          if (items[i].product_id === giftNote.productId) {
            existingLine = i + 1;
            break;
          }
        }

        var props = { 'Gift message': message, _gift: 'true' };
        var endpoint;
        var payload;

        if (existingLine) {
          endpoint = '/cart/change.js';
          payload = { line: existingLine, quantity: 1, properties: props };
        } else {
          endpoint = '/cart/add.js';
          payload = { id: giftNote.variantId, quantity: 1, properties: props };
        }

        if (sectionsParam) payload.sections = sectionsParam;

        return origFetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(payload)
        });
      })
      .then(function (res) {
        return res && res.ok ? res.json() : null;
      })
      .then(function (data) {
        if (data && data.sections) applySections(data.sections);
      })
      .catch(function () {
        // Never surface an error to the buyer over the gift-note line: the box itself already added
        // successfully by the time this runs.
      });
  }

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';

    if (!CART_ADD_RE.test(url) || !init || !(init.body instanceof FormData)) {
      return origFetch(input, init);
    }

    var body = init.body;
    var giftFlag = body.get(GIFT_KEY);
    var rawMessage = body.get(MSG_KEY);
    var message = rawMessage ? String(rawMessage).trim() : '';

    if (giftFlag !== 'true' || !message) {
      // Plain box add (no message, or not a gifting-template submission at all): leave untouched.
      return origFetch(input, init);
    }

    var sectionsParam = body.get('sections') || '';

    return resolveGiftNote().then(function (giftNote) {
      if (!giftNote) {
        // Gift-note product not live yet: fall back to exactly today's behavior, request untouched.
        return origFetch(input, init);
      }

      // Move the message off the box line before it's sent; the box keeps only the _gift marker.
      body.delete(MSG_KEY);

      return origFetch(input, init).then(function (response) {
        if (response.ok) {
          upsertGiftNoteLine(message, giftNote, sectionsParam);
        }
        return response;
      });
    });
  };
})();
